/**
 * PaymentManagement Component
 * Dashboard per admin per gestire richieste di pagamento e verificare bonifici
 */

import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Spinner, Table, Badge, Modal, Form, Nav, Tab, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const PaymentManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');

  // Bonifici state
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState('');
  const [verifyDecision, setVerifyDecision] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Richieste di pagamento state
  const [allRequests, setAllRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState(null);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsFilter, setRequestsFilter] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  useEffect(() => {
    if (activeTab === 'pending') fetchPendingTransfers();
    if (activeTab === 'requests') fetchAllRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchPendingTransfers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payments/bank-transfer/pending');
      if (response.data.success) {
        setPendingTransfers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching pending transfers:', err);
      setError('Errore nel caricamento dei bonifici');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRequests = async (status = requestsFilter) => {
    try {
      setRequestsLoading(true);
      setRequestsError(null);
      const params = status ? `?status=${status}` : '';
      const response = await api.get(`/payments/requests${params}`);
      if (response.data.success) {
        setAllRequests(response.data.requests || []);
        setRequestsTotal(response.data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching payment requests:', err);
      setRequestsError('Errore nel caricamento delle richieste di pagamento');
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Annullare questa richiesta di pagamento?')) return;
    setCancellingId(requestId);
    try {
      await api.patch(`/payments/requests/${requestId}/cancel`);
      await fetchAllRequests();
    } catch (err) {
      alert(err.response?.data?.message || 'Errore durante la cancellazione');
    } finally {
      setCancellingId(null);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    if (!window.confirm(`Vuoi davvero cambiare lo stato in "${newStatus}"?`)) return;
    setUpdatingStatusId(requestId);
    try {
      await api.patch(`/payments/requests/${requestId}/status`, { status: newStatus });
      await fetchAllRequests();
    } catch (err) {
      alert(err.response?.data?.message || "Errore durante l'aggiornamento dello stato");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleVerifyClick = (payment) => {
    setSelectedPayment(payment);
    setVerifyDecision(true);
    setVerifyNotes('');
    setShowVerifyModal(true);
  };

  const handleVerifySubmit = async () => {
    if (!selectedPayment) return;

    setProcessing(true);
    try {
      const response = await api.post(
        `/payments/bank-transfer/${selectedPayment.id}/verify`,
        {
          verified: verifyDecision,
          notes: verifyNotes
        }
      );

      if (response.data.success) {
        // Refresh lista
        await fetchPendingTransfers();
        setShowVerifyModal(false);
        alert(verifyDecision ? 'Bonifico verificato con successo' : 'Bonifico respinto');
      }
    } catch (err) {
      console.error('Error verifying transfer:', err);
      alert('Errore durante la verifica');
    } finally {
      setProcessing(false);
    }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const response = await api.get(
        `/payments/bank-transfer/${paymentId}/receipt`,
        { responseType: 'blob' }
      );
      
      // Crea un link per il download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ricevuta-${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading receipt:', err);
      alert('Errore durante il download della ricevuta');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'secondary',
      awaiting_payment: 'warning',
      processing: 'info',
      awaiting_verification: 'warning',
      succeeded: 'success',
      failed: 'danger',
      cancelled: 'secondary'
    };

    const labels = {
      pending: 'In attesa',
      awaiting_payment: 'Attesa pagamento',
      processing: 'Elaborazione',
      awaiting_verification: 'Da verificare',
      succeeded: 'Completato',
      failed: 'Fallito',
      cancelled: 'Annullato'
    };

    return (
      <Badge bg={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (!['admin', 'uisp_national', 'uisp_regional', 'uisp_territorial'].includes(user?.role)) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          Non sei autorizzato ad accedere a questa sezione
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h2 style={{ color: '#006e39' }}>
        <i className="bi bi-cash-stack me-2"></i>
        Gestione Pagamenti
      </h2>
      <p className="text-muted">Verifica bonifici e gestisci richieste di pagamento</p>

      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="pending">
              Bonifici da verificare
              {pendingTransfers.length > 0 && (
                <Badge bg="danger" className="ms-2">{pendingTransfers.length}</Badge>
              )}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="requests">Richieste di pagamento</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Tab Bonifici da verificare */}
          <Tab.Pane eventKey="pending">
            <Card className="shadow-sm">
              <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }}>
                <h5 className="mb-0">
                  <i className="bi bi-clock-history me-2"></i>
                  Bonifici in attesa di verifica
                </h5>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center p-4">
                    <Spinner animation="border" style={{ color: '#006e39' }} />
                    <p className="mt-3">Caricamento...</p>
                  </div>
                ) : error ? (
                  <Alert variant="danger">{error}</Alert>
                ) : pendingTransfers.length === 0 ? (
                  <Alert variant="info" className="mb-0">
                    <i className="bi bi-check-circle me-2"></i>
                    Nessun bonifico in attesa di verifica
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Data Upload</th>
                          <th>Associazione</th>
                          <th>Importo</th>
                          <th>Riferimento</th>
                          <th>Stato</th>
                          <th>Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingTransfers.map(payment => (
                          <tr key={payment.id}>
                            <td>#{payment.id}</td>
                            <td>
                              {payment.bank_transfer_upload_date 
                                ? new Date(payment.bank_transfer_upload_date).toLocaleDateString('it-IT') 
                                : '-'}
                            </td>
                            <td>
                              {payment.association_id 
                                ? `Associazione #${payment.association_id}` 
                                : 'N/D'}
                            </td>
                            <td>
                              <strong>€{parseFloat(payment.amount).toFixed(2)}</strong>
                            </td>
                            <td>
                              {payment.bank_transfer_reference || 
                                <span className="text-muted">-</span>}
                            </td>
                            <td>{getStatusBadge(payment.status)}</td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => downloadReceipt(payment.id)}
                                  title="Scarica ricevuta"
                                >
                                  <i className="bi bi-download"></i>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => handleVerifyClick(payment)}
                                  title="Verifica"
                                >
                                  <i className="bi bi-check-circle"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Tab Richieste di pagamento */}
          <Tab.Pane eventKey="requests">
            <Card className="shadow-sm">
              <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }} className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="bi bi-receipt me-2"></i>
                  Richieste di pagamento
                  {requestsTotal > 0 && (
                    <Badge bg="light" text="dark" className="ms-2">{requestsTotal}</Badge>
                  )}
                </h5>
                <Button
                  size="sm"
                  variant="light"
                  onClick={() => navigate('/admin/payments/create')}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  Nuova richiesta
                </Button>
              </Card.Header>
              <Card.Body>
                {/* Filtro per stato */}
                <div className="d-flex gap-2 mb-3 flex-wrap">
                  {[
                    { value: '', label: 'Tutti' },
                    { value: 'awaiting_payment', label: 'In attesa pagamento' },
                    { value: 'awaiting_verification', label: 'Da verificare' },
                    { value: 'succeeded', label: 'Completati' },
                    { value: 'cancelled', label: 'Annullati' },
                    { value: 'failed', label: 'Falliti' },
                  ].map(f => (
                    <Button
                      key={f.value}
                      size="sm"
                      variant={requestsFilter === f.value ? 'success' : 'outline-secondary'}
                      onClick={() => {
                        setRequestsFilter(f.value);
                        fetchAllRequests(f.value);
                      }}
                    >
                      {f.label}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => fetchAllRequests(requestsFilter)}
                    className="ms-auto"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </Button>
                </div>

                {requestsLoading ? (
                  <div className="text-center p-4">
                    <Spinner animation="border" style={{ color: '#006e39' }} />
                    <p className="mt-3">Caricamento...</p>
                  </div>
                ) : requestsError ? (
                  <Alert variant="danger">{requestsError}</Alert>
                ) : allRequests.length === 0 ? (
                  <Alert variant="info" className="mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    Nessuna richiesta di pagamento trovata.{' '}
                    <Button variant="link" className="p-0" onClick={() => navigate('/admin/payments/create')}>
                      Creane una nuova
                    </Button>
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Data</th>
                          <th>Evento</th>
                          <th>Assoc.</th>
                          <th>Importo</th>
                          <th>Scadenza</th>
                          <th>Stato</th>
                          <th>Email</th>
                          <th>Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allRequests.map(req => (
                          <tr key={req.id}>
                            <td>#{req.id}</td>
                            <td className="text-nowrap">
                              {new Date(req.created_at).toLocaleDateString('it-IT')}
                            </td>
                            <td>
                              {req.event_id
                                ? <span className="badge bg-secondary">Ev.{req.event_id}</span>
                                : '-'}
                            </td>
                            <td>
                              {req.association_id
                                ? <span className="badge bg-info text-dark">Ass.{req.association_id}</span>
                                : '-'}
                            </td>
                            <td>
                              <strong>€{parseFloat(req.amount).toFixed(2)}</strong>
                            </td>
                            <td className="text-nowrap">
                              {req.payment_deadline
                                ? new Date(req.payment_deadline).toLocaleDateString('it-IT')
                                : <span className="text-muted">-</span>}
                            </td>
                            <td>{getStatusBadge(req.status)}</td>
                            <td>
                              {req.email_sent
                                ? <Badge bg="success" title={req.email_sent_at ? new Date(req.email_sent_at).toLocaleString('it-IT') : ''}>
                                    <i className="bi bi-check"></i> Inviata
                                  </Badge>
                                : <Badge bg="secondary">Non inviata</Badge>}
                            </td>
                            <td>
                              <div className="d-flex gap-1 align-items-center">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  title="Visualizza evento"
                                  onClick={() => navigate(`/admin/events/${req.event_id}`)}
                                  disabled={!req.event_id}
                                >
                                  <i className="bi bi-eye"></i>
                                </Button>
                                
                                <Dropdown>
                                  <Dropdown.Toggle size="sm" variant="outline-secondary" title="Cambia stato" disabled={updatingStatusId === req.id}>
                                    {updatingStatusId === req.id ? <Spinner animation="border" size="sm" /> : <i className="bi bi-pencil"></i>}
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => handleUpdateStatus(req.id, 'pending')} disabled={req.status === 'pending'}>In attesa</Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleUpdateStatus(req.id, 'succeeded')} disabled={req.status === 'succeeded'}>Pagato</Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleUpdateStatus(req.id, 'failed')} disabled={req.status === 'failed'}>Fallito</Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item className="text-danger" onClick={() => handleUpdateStatus(req.id, 'cancelled')} disabled={req.status === 'cancelled'}>Annullato</Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>

                                {!['succeeded', 'cancelled'].includes(req.status) && (
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    title="Annulla richiesta"
                                    onClick={() => handleCancelRequest(req.id)}
                                    disabled={cancellingId === req.id}
                                  >
                                    {cancellingId === req.id
                                      ? <Spinner animation="border" size="sm" />
                                      : <i className="bi bi-x-circle"></i>}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Modal verifica bonifico */}
      <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Verifica Bonifico</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPayment && (
            <>
              <Alert variant="info">
                <h6>Dettagli Bonifico</h6>
                <div className="row">
                  <div className="col-md-6">
                    <p className="mb-1"><strong>ID Pagamento:</strong> #{selectedPayment.id}</p>
                    <p className="mb-1">
                      <strong>Importo:</strong> €{parseFloat(selectedPayment.amount).toFixed(2)}
                    </p>
                    <p className="mb-1">
                      <strong>Associazione:</strong> #{selectedPayment.association_id}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1">
                      <strong>Data upload:</strong>{' '}
                      {selectedPayment.bank_transfer_upload_date 
                        ? new Date(selectedPayment.bank_transfer_upload_date).toLocaleString('it-IT') 
                        : '-'}
                    </p>
                    <p className="mb-1">
                      <strong>Riferimento:</strong>{' '}
                      {selectedPayment.bank_transfer_reference || 'Nessuno'}
                    </p>
                  </div>
                </div>
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label><strong>Decisione</strong></Form.Label>
                <div>
                  <Form.Check
                    type="radio"
                    id="verify-approve"
                    label="✅ Approva bonifico (pagamento confermato)"
                    name="decision"
                    checked={verifyDecision === true}
                    onChange={() => setVerifyDecision(true)}
                  />
                  <Form.Check
                    type="radio"
                    id="verify-reject"
                    label="❌ Respingi bonifico (pagamento non valido)"
                    name="decision"
                    checked={verifyDecision === false}
                    onChange={() => setVerifyDecision(false)}
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Note {verifyDecision === false && <span className="text-danger">*</span>}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={verifyNotes}
                  onChange={(e) => setVerifyNotes(e.target.value)}
                  placeholder={
                    verifyDecision 
                      ? "Aggiungi note opzionali sulla verifica..." 
                      : "Specifica il motivo del rifiuto (obbligatorio)"
                  }
                  required={!verifyDecision}
                />
              </Form.Group>

              <Alert variant={verifyDecision ? 'success' : 'warning'}>
                <strong>⚠️ Attenzione:</strong>{' '}
                {verifyDecision 
                  ? 'Confermando, il pagamento sarà registrato come completato.'
                  : 'Rifiutando, l\'associazione dovrà caricare una nuova ricevuta o effettuare nuovamente il pagamento.'}
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVerifyModal(false)} disabled={processing}>
            Annulla
          </Button>
          <Button
            variant={verifyDecision ? 'success' : 'danger'}
            onClick={handleVerifySubmit}
            disabled={processing || (!verifyDecision && !verifyNotes.trim())}
          >
            {processing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Elaborazione...
              </>
            ) : (
              <>
                {verifyDecision ? (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Approva Bonifico
                  </>
                ) : (
                  <>
                    <i className="bi bi-x-circle me-2"></i>
                    Respingi Bonifico
                  </>
                )}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PaymentManagement;
